/** Stroke icons (24px grid, currentColor). Decorative: the button around them carries the label. */
const base = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true } as const;

export const MicIcon = ({ off = false }: { off?: boolean }) => (
  <svg {...base}>
    <rect x="9" y="3" width="6" height="12" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    {off && <path d="M4 4l16 16" />}
  </svg>
);
export const FlipIcon = () => (
  <svg {...base}>
    <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
    <path d="M9.5 13.5a2.5 2.5 0 0 1 4.5-1.5M14.5 12.5a2.5 2.5 0 0 1-4.5 1.5" />
  </svg>
);
export const ScreenIcon = () => (
  <svg {...base}>
    <rect x="3" y="4" width="18" height="12" rx="2" />
    <path d="M8 20h8M12 16v4" />
  </svg>
);
export const CameraIcon = () => (
  <svg {...base}>
    <rect x="3" y="6" width="13" height="12" rx="2" />
    <path d="M16 10l5-3v10l-5-3" />
  </svg>
);
export const CloseIcon = () => (
  <svg {...base} strokeWidth={2.5}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);
export const LockIcon = ({ open = false, size = 18 }: { open?: boolean; size?: number }) => (
  <svg {...base} width={size} height={size}>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d={open ? 'M8 11V7a4 4 0 0 1 7.5-2' : 'M8 11V7a4 4 0 0 1 8 0v4'} />
  </svg>
);
export const TrashIcon = () => (
  <svg {...base} width={18} height={18}>
    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
  </svg>
);
export const SoundIcon = () => (
  <svg {...base} width={18} height={18}>
    <path d="M4 9h4l5-4v14l-5-4H4z" />
    <path d="M17 9a4 4 0 0 1 0 6" />
  </svg>
);
export const SendIcon = () => (
  <svg {...base} width={20} height={20}>
    <path d="M4 12l16-8-6 16-3-7z" />
    <path d="M11 13l9-9" />
  </svg>
);
export const GearIcon = () => (
  <svg {...base} width={20} height={20}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v3M12 19v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M2 12h3M19 12h3M4.9 19.1L7 17M17 7l2.1-2.1" />
  </svg>
);
export const PinIcon = () => (
  <svg {...base} width={16} height={16}>
    <path d="M9 4h6l-1 6 3 3H7l3-3zM12 13v7" />
  </svg>
);
export const SmileIcon = () => (
  <svg {...base} width={20} height={20}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 14a4 4 0 0 0 7 0M9 9.5h.01M15 9.5h.01" />
  </svg>
);
export const ShareIcon = ({ size = 22 }: { size?: number }) => (
  <svg {...base} width={size} height={size}>
    <path d="M12 3v12M7 8l5-5 5 5" />
    <path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
  </svg>
);
export const ChatIcon = () => (
  <svg {...base} width={20} height={20}>
    <path d="M4 5h16v11H9l-5 4z" />
  </svg>
);
export const EyeIcon = ({ size = 16 }: { size?: number }) => (
  <svg {...base} width={size} height={size}>
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
/** Marks something the AI decided (the stream's category). */
export const SparkleIcon = ({ size = 13 }: { size?: number }) => (
  <svg {...base} width={size} height={size} fill="currentColor" stroke="none">
    <path d="M12 2l2.2 6.6L21 11l-6.8 2.4L12 20l-2.2-6.6L3 11l6.8-2.4z" />
  </svg>
);
export const ThumbUpIcon = ({ filled = false, size = 18 }: { filled?: boolean; size?: number }) => (
  <svg {...base} width={size} height={size} fill={filled ? 'currentColor' : 'none'}>
    <path d="M7 10v11H4V10zM7 10l4-7a2 2 0 0 1 3 2l-1 5h6a2 2 0 0 1 2 2.3l-1.4 7A2 2 0 0 1 17.6 21H7" />
  </svg>
);
export const ThumbDownIcon = ({ filled = false, size = 18 }: { filled?: boolean; size?: number }) => (
  <svg {...base} width={size} height={size} fill={filled ? 'currentColor' : 'none'}>
    <path d="M17 14V3h3v11zM17 14l-4 7a2 2 0 0 1-3-2l1-5H5a2 2 0 0 1-2-2.3l1.4-7A2 2 0 0 1 6.4 3H17" />
  </svg>
);
/** An outside link (profile links on a channel). */
export const LinkIcon = ({ size = 15 }: { size?: number }) => (
  <svg {...base} width={size} height={size} strokeWidth={2.4}>
    <path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1" />
    <path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1" />
  </svg>
);
export const CameraPlusIcon = ({ size = 18 }: { size?: number }) => (
  <svg {...base} width={size} height={size}>
    <path d="M4 8h3l2-3h6l2 3h3v11H4z" />
    <circle cx="12" cy="13" r="3.5" />
  </svg>
);
export const PencilIcon = ({ size = 16 }: { size?: number }) => (
  <svg {...base} width={size} height={size}>
    <path d="M4 20h4L19 9l-4-4L4 16z" />
    <path d="M13.5 6.5l4 4" />
  </svg>
);

/* Video player (components/PlayerControls.tsx). Solid shapes read better over video than strokes. */
const solid = { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'currentColor', 'aria-hidden': true } as const;

export const PlayIcon = ({ size = 24 }: { size?: number }) => (
  <svg {...solid} width={size} height={size}>
    <path d="M7.5 4.6v14.8a1 1 0 0 0 1.53.85l11.8-7.4a1 1 0 0 0 0-1.7L9.03 3.75A1 1 0 0 0 7.5 4.6z" />
  </svg>
);
/** Two bars leaning forward like the logo's speed stripes. */
export const PauseIcon = ({ size = 24 }: { size?: number }) => (
  <svg {...solid} width={size} height={size}>
    <path d="M8.6 4h3.6L9.4 20H5.8zM17.6 4h3.6l-2.8 16h-3.6z" />
  </svg>
);
export const ReplayIcon = ({ size = 24 }: { size?: number }) => (
  <svg {...base} width={size} height={size} strokeWidth={2.4}>
    <path d="M4 12a8 8 0 1 0 2.6-5.9" />
    <path d="M4 4v4.5h4.5" />
  </svg>
);
export const VolumeIcon = ({ level }: { level: 'off' | 'low' | 'high' }) => (
  <svg {...base}>
    <path d="M3.5 9h4l5-4.2v14.4l-5-4.2h-4z" fill="currentColor" />
    {level === 'off' ? <path d="M16.5 9.5l5 5M21.5 9.5l-5 5" /> : <path d="M16 9a4 4 0 0 1 0 6" />}
    {level === 'high' && <path d="M18.8 6.2a8 8 0 0 1 0 11.6" />}
  </svg>
);
export const FullscreenIcon = ({ exit = false }: { exit?: boolean }) => (
  <svg {...base} strokeWidth={2.4}>
    <path d={exit ? 'M9 4v5H4M15 4v5h5M20 15h-5v5M4 15h5v5' : 'M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5'} />
  </svg>
);
export const PipIcon = () => (
  <svg {...base}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <rect x="12" y="11.5" width="6" height="4.5" rx="1" fill="currentColor" stroke="none" />
  </svg>
);
export const AirPlayIcon = () => (
  <svg {...base}>
    <path d="M6.5 17H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-1.5" />
    <path d="M12 14.5l5 6H7z" fill="currentColor" stroke="none" />
  </svg>
);
/** An eight-tooth cog: the player's settings (quality, speed, stats). */
const COG = (() => {
  const d: string[] = [];
  for (let i = 0; i < 32; i++) {
    const a = ((i - 0.5) / 32) * Math.PI * 2;
    const r = i % 4 < 2 ? 10 : 7.4;
    d.push(`${(12 + r * Math.cos(a)).toFixed(2)} ${(12 + r * Math.sin(a)).toFixed(2)}`);
  }
  return `M${d.join('L')}z`;
})();
export const SettingsIcon = () => (
  <svg {...base}>
    <path d={COG} />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
export const CheckIcon = ({ size = 18 }: { size?: number }) => (
  <svg {...base} width={size} height={size} strokeWidth={2.8}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);
export const ChevronIcon = ({ back = false, size = 18 }: { back?: boolean; size?: number }) => (
  <svg {...base} width={size} height={size} strokeWidth={2.6}>
    <path d={back ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
  </svg>
);
